import { getFileUrl } from '@/clients/http'
import { FolderTree, Pencil, Trash2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'

import { useCategoriesTable } from '@/hooks/features/categories'
import { useDataTable } from '@/hooks/ui'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'

import {
  ActionDropdown,
  ConfirmDialog,
  PageHeader,
  StatsCards,
  StatusBadge,
} from '@/components/atoms'

import { ProductCategoryModel } from '@/types'

import { CategoriesFilters } from './CategoriesFilters'
import { CategoryDialog } from './CategoryDialog'

type TableColumns =
  'image' | 'name' | 'parent' | 'description' | 'products_count' | 'status' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'image',
  'name',
  'parent',
  'description',
  'products_count',
  'status',
  'actions',
]

export const CategoriesTable = () => {
  const {
    loading,
    data,
    pagination,
    formMethods,
    stats,
    mode,
    categoryDialogOpen,
    editCategory,
    deleteDialogOpen,
    categoryToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateCategory,
    handleUpdateCategory,
    toggleStatus,
  } = useCategoriesTable()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    ProductCategoryModel.ProductCategory,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'image',
        columnLabel: () => 'Image',
        cell: (data) => (
          <div className="flex items-center">
            {data?.image?.path ? (
              <img
                src={getFileUrl(data?.image?.path)}
                alt={data?.name}
                className="size-10 rounded-md border border-border-subtle object-cover"
              />
            ) : (
              <div className="flex size-10 items-center justify-center rounded-md border border-border-subtle bg-muted">
                <FolderTree className="size-4 text-muted-foreground" aria-hidden="true" />
              </div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'name',
        columnLabel: () => 'Name',
        cell: (data) => <span className="font-medium text-ink">{data?.name}</span>,
        hasSorting: true,
      },
      {
        accessorKey: 'parent',
        columnLabel: () => 'Parent',
        cell: (data) => <span className="text-muted-foreground">{data?.parent?.name || '—'}</span>,
      },
      {
        accessorKey: 'products_count',
        columnLabel: () => 'Products',
        cell: (data) => <span className="tabular-nums text-ink">{data?.products_count}</span>,
      },
      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <StatusBadge
              type="product"
              status={data?.status === 'active' ? 'active' : 'inactive'}
            />
            <Switch
              checked={data?.status === 'active'}
              onCheckedChange={() => toggleStatus(data)}
            />
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end">
            <ActionDropdown
              items={[
                {
                  label: 'View',
                  permission: 'categories:details',
                  icon: FolderTree,
                  onClick: () => openViewDialog(data),
                },
                {
                  label: 'Edit',
                  permission: 'categories:update',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Delete',
                  permission: 'categories:delete',
                  icon: Trash2,
                  variant: 'destructive',
                  onClick: () => handleDeleteClick(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Categories"
        description="Organize products into categories."
        onAddClick={openCreateDialog}
        addPermission="categories:create"
        addButtonText="Add category"
      />

      <StatsCards
        stats={[
          {
            title: 'Total categories',
            value: stats.totalCategories,
            icon: FolderTree,
          },
          {
            title: 'Active categories',
            value: stats.activeCategories,
          },
          {
            title: 'Inactive categories',
            value: stats.inactiveCategories,
          },
        ]}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />
              <FormProvider {...formMethods}>
                <CategoriesFilters />
              </FormProvider>
            </div>
            <ColumnVisibilityDropdown
              columns={columns}
              onColumnVisibilityChange={changeColumnVisibility}
              disabledColumns={disabledColumns}
            />
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No categories found"
              btnText="Add category"
              openCreateDialog={openCreateDialog}
              createPermission="categories:create"
            />
          ) : (
            <>
              <DataTable columns={columns} data={data} loading={loading} />

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Category Dialog */}
      <CategoryDialog
        open={categoryDialogOpen}
        mode={mode}
        editCategory={editCategory}
        closeDialog={closeDialog}
        onCreate={handleCreateCategory}
        onUpdate={handleUpdateCategory}
        loading={createLoading || updateLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete category?"
        description={`This will permanently delete the category "${categoryToDelete?.name}". This action cannot be undone.`}
        confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />
    </div>
  )
}
